import React from "react";

import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Avatar,
  Box,
  Button,
  Fab,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TableCell,
  TableRow,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import pinnedClients from "@/components/Sidebar/ClickComponents/Client/store/PinnedClients";
import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import useFragment from "@/hooks/useFragment";
import { format } from "@/utils/datetime";

import TimelineDrawer from "./TimelineDrawer";
import TrackingStatusSelect from "./TrackingStatusSelect";

interface Props {
  id: string;
}

const clientRowFragment = graphql(`
  fragment ClientRow on Client {
    id
    email
    profile {
      id
      fullName
      mobileNumber
      avatarUrl(variant: THUMB)
    }
    createdAt
    trackingStatus
    selectedPlanName
    origin
    month
    lastEvent {
      id
      type
      message
      createdAt
    }
  }
`);

export const DIVIDER_SIZE = 2;
export const DIVIDER_PADDING = 20;

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    textDecoration: "none",
  },
  cell: {
    borderBottom: `8px solid ${theme.palette.background.default}`,
    padding: "16px",
    backgroundColor: "#fff",
    fontWeight: "600",
    color: "#112340",
    "& + &": {
      paddingLeft: 0,
    },
  },
  divider: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    borderLeft: `${DIVIDER_SIZE}px solid #EDF0F5`,
    paddingLeft: DIVIDER_PADDING,
    height: "100%",
  },
  pinButton: {
    marginRight: 16,
    flexShrink: 0,
  },
  nameContainer: {
    padding: 0,
  },
  month: {
    whiteSpace: "nowrap",
  },
  lastEventMessage: {
    fontSize: 14,
    fontWeight: "600",
    letterSpacing: "-0.5px",
    color: "#525252",
    whiteSpace: "pre-wrap",
  },
  lastEventAt: {
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: "-0.5px",
    color: "#7D8797",
  },
}));

const ClientRow: React.FC<Props> = ({ id }) => {
  const { classes, cx } = useStyles();
  const [client] = useFragment(clientRowFragment, `Client:${id}`);
  const timelineDrawer = useDialogState();
  return (
    <>
      <TableRow className={classes.root}>
        <TableCell className={classes.cell}>
          <Box display="flex" alignItems="center">
            <Fab
              color={pinnedClients.has(id) ? "primary" : "default"}
              size="small"
              onClick={() => pinnedClients.toggle(id)}
              sx={{ marginRight: "16px", flexShrink: 0 }}
            >
              <PushPinOutlinedIcon fontSize="small" />
            </Fab>
            <ListItem className={classes.nameContainer}>
              <ListItemAvatar>
                <Avatar src={client?.profile?.avatarUrl ?? undefined} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Link
                    component={RouterLink}
                    to={`/clients/${client?.id}`}
                    underline="hover"
                    color="#525252"
                    fontWeight="600"
                  >
                    {client?.profile?.fullName}
                  </Link>
                }
                secondary={
                  <>
                    <div>{client?.email}</div>
                    <div>{client?.profile?.mobileNumber}</div>
                  </>
                }
              />
            </ListItem>
          </Box>
        </TableCell>
        <TableCell className={classes.cell}>
          <div className={classes.divider}>
            <TrackingStatusSelect
              clientId={id}
              value={client?.trackingStatus}
            />
          </div>
        </TableCell>
        <TableCell className={classes.cell}>
          <div className={classes.divider}>
            {format({ datetime: client?.createdAt }, "MMM dd, yyyy")}
          </div>
        </TableCell>
        <TableCell className={classes.cell}>
          <div className={classes.divider}>
            {client?.selectedPlanName ?? "N/A"}
          </div>
        </TableCell>
        <TableCell className={classes.cell}>
          <div className={classes.divider}>
            {origin === "marketplace" ? "Paid" : "Free"} Client
          </div>
        </TableCell>
        <TableCell className={classes.cell}>
          <div className={cx(classes.divider, classes.month)}>
            Month {client?.month}
          </div>
        </TableCell>
        <TableCell className={classes.cell}>
          <div className={classes.divider}>
            <div className={classes.lastEventMessage}>
              {client?.lastEvent?.message}
            </div>
            <div className={classes.lastEventAt}>
              {format({ datetime: client?.lastEvent?.createdAt }, "Pp")}
            </div>
            <Button variant="text" onClick={timelineDrawer.show}>
              View More
            </Button>
          </div>
        </TableCell>
      </TableRow>
      <TimelineDrawer {...timelineDrawer.props} clientId={client?.id ?? ""} />
    </>
  );
};

export default ClientRow;
